/** The Overview tab's secondary blocks: the facts row, the data-quality bar, and the basis.
 *
 * Split out of `Inventory.tsx` when that screen became tabbed (journeys 3 and 5), so the register
 * keeps its own file's worth of wiring and these can be read - and tested - without it. Three rules
 * each of them keeps, and each is something a tidier layout would undo:
 *
 * - **A fact that has no source is not drawn.** The prior-period tile exists only when a prior
 *   inventory was read *and* the same scopes are assessed in both: comparing a total that includes
 *   Scope 3 with one that does not is an arrow on the difference between two methods. Absent, never
 *   zero (the home screen's rule, `readinessModel`).
 * - **The quality bar counts figures, not tonnes**, and says so in its own subtitle. A grade is a
 *   property of a whole source record; weighting by tonnes would need the allocated split this
 *   response does not carry, and an unweighted count dressed as a share of emissions is a wrong
 *   number that looks right.
 * - **Honesty copy is ranked, not removed.** The energy line (a figure, with what it excludes) stays
 *   visible; the longer basis sentences sit behind one collapsed disclosure, every word kept.
 */

import { CaretRight } from "@phosphor-icons/react";

import type { Inventory } from "../api/queries";
import { KpiTile } from "../components/KpiTile";
import { Panel } from "../components/Panel";
import { LazyDetails } from "../components/ui/LazyDetails";
import { quantity, unitLabel } from "../lib/format";
import own from "./Inventory.module.css";
import styles from "./InventoryOverview.module.css";
import { Delta } from "./ReadinessKpis";
import type { KpiDelta } from "./ReadinessKpis";
import { gradeLabel } from "./register";

function cls(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

const count = (n: number) => quantity(String(n));

/** A grade's own token, so the bar and the register's chips are one ramp. A grade the ramp does not
 * know (the server may grow one) takes the lightest rather than no fill. */
const GRADE_VAR: Record<string, string> = {
  A: "var(--q-a)",
  B: "var(--q-b)",
  C: "var(--q-c)",
  D: "var(--q-d)",
};

export interface Fact {
  key: string;
  label: string;
  value: string;
  unit?: string;
  note?: string;
  delta?: KpiDelta;
  /** The drill: opens the register on the rows the number counts. */
  drill?: { label: string; onOpen: () => void };
}

/** Facts about the window, as `KpiTile`s. Static (no figure button): the receipt for a total is the
 * register's row, and a tile that opened nothing must not be dressed as a link. */
export function InventoryFacts({ facts }: { facts: readonly Fact[] }) {
  if (facts.length === 0) return null;
  return (
    <section className={styles.facts} aria-label="Facts about this window" data-testid="inventory-facts">
      {facts.map((fact) => (
        <KpiTile
          key={fact.key}
          kpi={fact.key}
          label={fact.label}
          value={fact.value}
          unit={fact.unit}
          note={fact.note}
          delta={fact.delta ? <Delta delta={fact.delta} /> : undefined}
          footer={
            fact.drill ? (
              <button type="button" className={styles.drill} onClick={fact.drill.onOpen}>
                {fact.drill.label}
              </button>
            ) : undefined
          }
        />
      ))}
    </section>
  );
}

/** Source figures by data-quality grade, as one segmented bar and a legend that says it in words.
 *
 * Each legend row is a button: choosing a grade opens the register already filtered to it, which is
 * the claim the home screen's *Metered* share makes and the one place it can be checked. The bar
 * itself is a picture of the legend and is hidden from assistive technology for that reason. */
export function QualityPanel({
  grades: counted,
  onOpenGrade,
}: {
  /** Figures at each grade, **as the server counted them** over the whole window (`gradeCountsOf`). */
  grades: Readonly<Record<"A" | "B" | "C" | "D", number>>;
  onOpenGrade: (grade: string) => void;
}) {
  const counts = (["A", "B", "C", "D"] as const)
    .map((grade) => ({ grade, n: counted[grade] }))
    .filter(({ n }) => n > 0);
  const total = counts.reduce((sum, { n }) => sum + n, 0);
  return (
    <Panel title="Data quality" sub="Source figures by grade - a count of figures, not of tonnes">
      {total === 0 ? (
        <p className={styles.empty} data-testid="inventory-quality">
          No source figures in this window to grade.
        </p>
      ) : (
        <div data-testid="inventory-quality">
          <div className={styles.bar} aria-hidden="true">
            {counts.map(({ grade, n }) => (
              <span
                key={grade}
                className={styles.segment}
                data-grade={grade}
                style={{
                  width: `${(n / total) * 100}%`,
                  background: GRADE_VAR[grade] ?? "var(--q-d)",
                }}
              />
            ))}
          </div>
          <ul className={styles.legend}>
            {counts.map(({ grade, n }) => (
              <li key={grade}>
                <button
                  type="button"
                  className={styles.gradeButton}
                  aria-label={`${gradeLabel(grade)}: ${count(n)} of ${count(total)} figures. Open them in the register`}
                  onClick={() => onOpenGrade(grade)}
                >
                  <span
                    className={styles.swatch}
                    style={{ background: GRADE_VAR[grade] ?? "var(--q-d)" }}
                    aria-hidden="true"
                  >
                    {grade}
                  </span>
                  <span className={styles.gradeName}>{gradeLabel(grade)}</span>
                  <span className={cls(styles.gradeCount, "num")}>
                    {count(n)} of {count(total)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Panel>
  );
}

/** What these totals include and leave out.
 *
 * The energy line stays in view because it is a figure (kWh, and which regime reports it comes from the workspace's own obligations) and what it excludes is
 * said in the same sentence as the figure it qualifies. The other three lines - on-site generation,
 * the value-chain sentence, the Scope 2 basis - are one disclosure, closed, with every sentence that
 * was a paragraph on the old screen still in it. */
export function BasisPanel({
  inventory,
  scope,
  method,
}: {
  inventory: Inventory;
  scope: string;
  method: string;
}) {
  const flows = inventory.energy.flows;
  const hasFlows = Boolean(flows && flows.generated !== "0");
  const showScope3 = (scope === "" || scope === "3") && Boolean(inventory.scope3);
  const showBasis = scope === "" || scope === "2";
  const hasMore = hasFlows || showScope3 || showBasis;
  return (
    <Panel title="Basis and limits" sub="What these totals include and leave out">
      {/* **Energy, beside the figures it comes from** (F09).
          SECR reports energy as well as emissions, and the number is not the sum of the kWh rows:
          gas arrives on a gross calorific value basis, supplies arrive in MWh, and fuel volumes
          cannot be converted at all without a published calorific value. Those are **disclosed on
          the line** rather than estimated, with the quantity - a partial figure that does not say so
          is the failure. */}
      <p className={cls(own.energy)} data-testid="energy">
        <strong>{quantity(inventory.energy.kwh, 0)} kWh</strong> of energy
        {inventory.energy.reported_under.length > 0
          ? `, reportable under ${inventory.energy.reported_under.join(" and ")}`
          : ""}
        {inventory.energy.omitted.length > 0 ? (
          <span className={cls(own.energyOmitted)}>
            {" "}
            · excludes{" "}
            {inventory.energy.omitted
              .map(
                (o) =>
                  `${quantity(o.quantity)} ${unitLabel(o.unit)} across ${o.records} record${o.records === 1 ? "" : "s"} (${o.because})`,
              )
              .join("; ")}
            . Their <strong>emissions</strong> are in the figures above; their energy is not.
          </span>
        ) : null}
      </p>
      {hasMore ? (
        <LazyDetails
          className={cls(own.disclosure, styles.basisDisclosure)}
          testId="inventory-basis"
          summary={
            <>
              <CaretRight size={16} weight="regular" aria-hidden="true" />
              Generation, value chain and Scope 2 basis
            </>
          }
        >
          <div className={cls(own.notes)}>
            {/* **Gross, never net** (ACC-08, verification log S-07). A site with solar is the case a
                net-metered bill gets wrong on its face: the meter reads consumption minus export, and
                the Scope 2 Guidance requires the gross purchase. The decomposition is on screen so a
                reader comparing this total with their bill can see *why* it is larger, rather than
                concluding the product has double-counted something. */}
            {hasFlows && flows ? (
              <div className={cls(own.noteRow)}>
                <span className={cls(own.noteKey)}>On-site generation</span>
                <p className={cls(own.energy)} data-testid="energy-flows">
                  {quantity(flows.imported, 0)} kWh purchased from the grid ·{" "}
                  {quantity(flows.generated, 0)} kWh generated on site, of which{" "}
                  {quantity(flows.self_consumed, 0)} kWh was used here and{" "}
                  {quantity(flows.exported, 0)} kWh was exported.
                  {flows.disclosures.map((line) => (
                    <span key={line} className={cls(own.energyOmitted)}>
                      {" "}
                      {line.replace(/\*\*/g, "")}
                    </span>
                  ))}
                </p>
              </div>
            ) : null}

            {/* The sentence the standard's requirement turns into, with its citation. Shown when
                Scope 3 is in view, because that is when a reader is asking the question - and it
                never states a figure. */}
            {showScope3 && inventory.scope3 ? (
              <div className={cls(own.noteRow)}>
                <span className={cls(own.noteKey)}>Value chain</span>
                <p className={cls(own.basis)} data-testid="scope3">
                  {inventory.scope3.sentence}
                </p>
              </div>
            ) : null}

            {/* The basis, always readable when Scope 2 is in view. Never a filter: changing it
                re-prices the same rows rather than hiding any. */}
            {showBasis ? (
              <div className={cls(own.noteRow)}>
                <span className={cls(own.noteKey)}>Scope 2 basis</span>
                <p className={cls(own.basis)}>
                  Scope 2 shown on the <strong>{method}-based</strong> method. Both methods are
                  reported and the two are never summed. Switch in the bar above to see the other.
                </p>
              </div>
            ) : null}
          </div>
        </LazyDetails>
      ) : null}
    </Panel>
  );
}
